if (!customElements.get('grouped-product-card-furnace')) {
class GroupedProductCardFurnace extends HTMLElement {
  constructor() {
    super();
    this.currentProductData = null;
    this.productDataCache = new Map();
    this.init();
  }

  init() {
    this.cacheProductData();
    this.bindEvents();
    this.setInitialState();
  }

  cacheProductData() {
    // Cache product data from script tag
    const productDataScript = this.querySelector('script[type="application/json"]');
    if (productDataScript) {
      try {
        const allProductData = JSON.parse(productDataScript.textContent);
        
        allProductData.forEach(productData => {
          this.productDataCache.set(productData.id, productData);
        });
        
        this.currentProductData = allProductData[0]; // Set first product as initial
      } catch (error) {
        console.error('Error parsing product data:', error);
      }
    }
  }

  bindEvents() {
    // Bind variant selector events with more specific targeting
    const variantSelectors = this.querySelectorAll('input[name^="variant-selector-"]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', this.handleVariantChange.bind(this));
    });
    
    // Bind product link clicks
    const productLinks = this.querySelectorAll('[data-product-link]');
    productLinks.forEach(link => {
      link.addEventListener('click', this.handleProductLinkClick.bind(this));
    });
    
    // Bind product image clicks
    const productImages = this.querySelectorAll('.card__inner > .card__content');
    productImages.forEach(img => {
      img.style.cursor = 'pointer';
      img.addEventListener('click', this.handleProductLinkClick.bind(this));
    });
  }

  setInitialState() {
    if (this.currentProductData) {
      // Ensure all variant selectors have a default selection
      this.ensureDefaultSelections();
      
      // Get current selections and find the best matching product
      const currentSelections = this.getCurrentSelections();
      
      if (Object.keys(currentSelections).length > 0) {
        const matchingProduct = this.findMatchingProduct(currentSelections, 1);
        if (matchingProduct && matchingProduct.id !== this.currentProductData.id) {
          this.currentProductData = matchingProduct;
        }
      }
      
      this.updateProductInfo(this.currentProductData);
      this.updateProductLinks(this.currentProductData.url);
      
      // Update selectors to match the current product (or ensure defaults)
      this.updateVariantSelectors(this.currentProductData);
      
      // Check availability on initial load after a short delay to ensure DOM is ready
      setTimeout(() => {
        this.updateVariantAvailability();
      }, 0);
    }
  }

  ensureDefaultSelections() {
    // Ensure each variant selector group has at least one option selected
    for (let i = 1; i <= 3; i++) {
      const selectors = this.querySelectorAll(`input[name^="variant-selector-${i}-"]`);
      if (selectors.length > 0) {
        const hasChecked = Array.from(selectors).some(s => s.checked);
        if (!hasChecked) {
          // Check the first option if nothing is checked
          selectors[0].checked = true;
        }
      }
    }
  }

  handleVariantChange(event) {
    // Check if the event target belongs to this specific card instance
    if (!this.contains(event.target)) {
      return;
    }
    
    // Determine which metafield selector was changed
    const changedMetafieldNumber = this.getChangedMetafieldNumber(event.target.name);
    
    // Reset dependent selectors based on hierarchy
    // If metafield 1 changed, reset 2 and 3
    // If metafield 2 changed, reset 3
    // Don't reset anything if metafield 3 changed (it's the lowest level)
    if (changedMetafieldNumber === 1) {
      this.resetMetafieldSelector(2);
      this.resetMetafieldSelector(3);
    } else if (changedMetafieldNumber === 2) {
      this.resetMetafieldSelector(3);
    }
    // No reset needed for metafield 3 - it's the final selection
    
    // Get all current selections for this card by metafield type
    const currentSelections = this.getCurrentSelections();
    
    // Find product that matches with hierarchical logic
    const matchingProduct = this.findMatchingProduct(currentSelections, changedMetafieldNumber);
    
    if (matchingProduct) {
      this.currentProductData = matchingProduct;
      this.updateProductInfo(matchingProduct);
      this.updateProductLinks(matchingProduct.url);
      this.updateVariantSelectors(matchingProduct);
    } else {
      // Keep current product, just update selectors to reflect it
      this.updateVariantSelectors(this.currentProductData);
    }
    
    // Always update availability after selection change
    this.updateVariantAvailability();
  }

  resetMetafieldSelector(metafieldNumber) {
    // Uncheck all radios for this metafield number and check the first available option
    const selectors = this.querySelectorAll(`input[name^="variant-selector-${metafieldNumber}-"]`);
    let firstAvailable = null;
    
    selectors.forEach(selector => {
      selector.checked = false;
      if (!firstAvailable && !selector.disabled) {
        firstAvailable = selector;
      }
    });
    
    // Check the first available option, or first option if all disabled
    if (firstAvailable) {
      firstAvailable.checked = true;
    } else if (selectors.length > 0) {
      selectors[0].checked = true;
    }
  }

  getChangedMetafieldNumber(inputName) {
    // Extract metafield number from name pattern: variant-selector-{number}-{product_id}
    const match = inputName.match(/variant-selector-(\d+)-/);
    return match ? parseInt(match[1]) : 1;
  }

  getCurrentSelections() {
    const selections = {};
    
    // Get all checked selectors grouped by metafield number
    const allSelectors = this.querySelectorAll('input[name^="variant-selector-"]:checked');
    
    allSelectors.forEach(selector => {
      // Extract metafield number from name pattern: variant-selector-{number}-{product_id}
      const match = selector.name.match(/variant-selector-(\d+)-/);
      if (match) {
        const metafieldNumber = match[1];
        const label = selector.nextElementSibling?.textContent?.trim() || '';
        selections[`metafield_${metafieldNumber}`] = label;
      }
    });
    
    return selections;
  }

  findMatchingProduct(targetSelections, changedMetafieldNumber = 1) {
    let bestMatch = null;
    let bestMatchScore = -1;
    
    this.productDataCache.forEach((product, id) => {
      let isMatch = true;
      let matchScore = 0;
      
      // Hierarchical matching logic based on which metafield changed
      // Priority: metafield_1 > metafield_2 > metafield_3
      
      const target1 = targetSelections.metafield_1;
      const target2 = targetSelections.metafield_2;
      const target3 = targetSelections.metafield_3;
      
      // Extract values from arrays if needed (Shopify metafields can be arrays)
      const product1 = Array.isArray(product.metafield_1) ? product.metafield_1[0] : product.metafield_1;
      const product2 = Array.isArray(product.metafield_2) ? product.metafield_2[0] : product.metafield_2;
      const product3 = Array.isArray(product.metafield_3) ? product.metafield_3[0] : product.metafield_3;
      
      // Metafield 1 matching (highest priority - always required if set)
      if (target1) {
        if (product1 === target1) {
          matchScore += 100; // Highest weight
        } else {
          isMatch = false; // metafield_1 mismatch = not a match
          return;
        }
      }
      
      // Metafield 2 matching (medium priority)
      if (target2) {
        if (changedMetafieldNumber === 1) {
          // Metafield 1 changed: prefer matching metafield_2 but don't require it
          if (product2 === target2) {
            matchScore += 10;
          }
        } else {
          // Metafield 2 or 3 changed: require metafield_2 match if we're past metafield 1
          if (product2 === target2) {
            matchScore += 10;
          } else if (changedMetafieldNumber >= 2) {
            isMatch = false; // metafield_2 mismatch when it should be locked
            return;
          }
        }
      }
      
      // Metafield 3 matching (lowest priority)
      if (target3) {
        if (changedMetafieldNumber <= 2) {
          // Metafield 1 or 2 changed: prefer matching metafield_3 but don't require it
          if (product3 === target3) {
            matchScore += 1;
          }
        } else {
          // Metafield 3 changed: require metafield_3 match
          if (product3 === target3) {
            matchScore += 1;
          } else {
            isMatch = false; // metafield_3 mismatch when it should be the selection
            return;
          }
        }
      }
      
      // Update best match if this product is better
      if (isMatch && matchScore > bestMatchScore) {
        bestMatch = product;
        bestMatchScore = matchScore;
      }
    });
    
    return bestMatch;
  }

  updateVariantSelectors(productData) {
    // Update radio button selections to match the product
    ['metafield_1', 'metafield_2', 'metafield_3'].forEach((metafieldKey, index) => {
      const metafieldNumber = index + 1;
      let productValue = productData[metafieldKey];
      
      // Extract value from array if needed (Shopify metafields can be arrays)
      if (Array.isArray(productValue)) {
        productValue = productValue[0];
      }
      
      const selectors = this.querySelectorAll(`input[name^="variant-selector-${metafieldNumber}-"]`);
      let matchFound = false;
      
      if (productValue) {
        // Find the radio button with this value by matching the label text
        selectors.forEach(selector => {
          const label = selector.nextElementSibling?.textContent?.trim();
          if (label === productValue) {
            selector.checked = true;
            matchFound = true;
          } else {
            selector.checked = false;
          }
        });
      }
      
      // If no match found and we have selectors, check the first available option
      if (!matchFound && selectors.length > 0) {
        const firstAvailable = Array.from(selectors).find(s => !s.disabled);
        if (firstAvailable) {
          firstAvailable.checked = true;
        } else if (selectors[0]) {
          // If all are disabled, still check the first one
          selectors[0].checked = true;
        }
      }
    });
  }

  handleProductLinkClick(event) {
    // Update href with current product URL before navigation
    if (this.currentProductData) {
      if (event.currentTarget.href !== undefined) {
        // It's a link element
        event.currentTarget.href = this.currentProductData.url;
      } else {
        // It's an image element, navigate directly
        window.location.href = this.currentProductData.url;
      }
    }
  }

  updateProductInfo(productData) {
    const productInfo = this.querySelector('.product-info');
    if (!productInfo) {
      return;
    }

    // Add updating class for animation
    productInfo.classList.add('updating');

    setTimeout(() => {
      // Update product title
      const titleElements = this.querySelectorAll('[data-product-title]');
      titleElements.forEach(el => {
        el.textContent = productData.title;
      });

      // Update product vendor
      const vendorElements = this.querySelectorAll('[data-product-vendor]');
      vendorElements.forEach(el => {
        if (productData.vendor) {
          el.textContent = productData.vendor;
        }
      });

      // Update product price - CDN JSON stores price like collection page: dollars = price/10000 (e.g. 8920000 = $892.00)
      const formatPriceFromJson = (priceFromJson) => {
        const num = Number(priceFromJson);
        if (typeof num !== 'number' || isNaN(num)) return '';
        const dollars = num / 10000;
        return '$' + dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      };
      const displayPrice = productData.price_formatted || (productData.price != null ? formatPriceFromJson(productData.price) : '');
      const displayComparePrice = productData.compare_at_price_formatted || (productData.compare_at_price != null ? formatPriceFromJson(productData.compare_at_price) : '');
      const priceElements = this.querySelectorAll('[data-product-price]');
      priceElements.forEach(el => {
        let priceHtml = '';
        if (productData.compare_at_price && productData.compare_at_price !== productData.price && displayComparePrice) {
          priceHtml = `
            <span class="product-price-compare">${displayComparePrice}</span>
            <span class="product-price-current">${displayPrice}</span>
          `;
        } else {
          priceHtml = `<span class="product-price-current">${displayPrice}</span>`;
        }
        el.innerHTML = priceHtml;
      });

      // Update product image
      const imageElements = this.querySelectorAll('[data-product-image]');
      imageElements.forEach(img => {
        if (productData.featured_image && productData.featured_image.url) {
          img.src = productData.featured_image.url;
          img.alt = productData.featured_image.alt || productData.title;
          // Generate srcset dynamically
          const baseUrl = productData.featured_image.url.split('?')[0];
          img.srcset = [
            `${baseUrl}?width=165 165w`,
            `${baseUrl}?width=360 360w`,
            `${baseUrl}?width=533 533w`,
            `${baseUrl}?width=720 720w`,
            `${baseUrl}?width=940 940w`,
            `${baseUrl}?width=1066 1066w`
          ].join(', ');
        }
      });

      // Update secondary image if exists
      const secondaryImages = this.querySelectorAll('[data-product-secondary-image]');
      secondaryImages.forEach(img => {
        if (productData.secondary_image) {
          img.src = productData.secondary_image.url;
          img.alt = productData.secondary_image.alt || productData.title;
          const baseUrl = productData.secondary_image.url.split('?')[0];
          img.srcset = [
            `${baseUrl}?width=165 165w`,
            `${baseUrl}?width=360 360w`,
            `${baseUrl}?width=533 533w`,
            `${baseUrl}?width=720 720w`,
            `${baseUrl}?width=940 940w`,
            `${baseUrl}?width=1066 1066w`
          ].join(', ');
        }
      });

      // Remove updating class and add updated class for animation
      productInfo.classList.remove('updating');
      productInfo.classList.add('updated');
      
      setTimeout(() => {
        productInfo.classList.remove('updated');
      }, 300);
    }, 150);
  }

  updateProductLinks(productUrl) {
    const productLinks = this.querySelectorAll('[data-product-link]');
    productLinks.forEach(link => {
      link.href = productUrl;
    });
    
    // Update View Product button
    const productButtons = this.querySelectorAll('[data-product-button]');
    productButtons.forEach(button => {
      button.href = productUrl;
    });
  }

  updateAvailabilityBadge(productData) {
    const badges = this.querySelectorAll('.card__badge span');
    badges.forEach(badge => {
      if (!productData.available) {
        badge.textContent = 'Sold out';
        badge.className = 'badge badge--bottom-left color-' + (window.badgeSettings?.soldOutScheme || 'accent-2');
      } else if (productData.compare_at_price > productData.price) {
        badge.textContent = 'Sale';
        badge.className = 'badge badge--bottom-left color-' + (window.badgeSettings?.saleScheme || 'accent-1');
      } else {
        badge.style.display = 'none';
      }
    });
  }

  updateQuickAddForms(productData) {
    // Get theme strings with fallbacks
    const strings = window.groupedProductCardStrings || {};
    const addToCartText = strings.addToCart || 'Add to cart';
    const soldOutText = strings.soldOut || 'Sold out';
    
    // Update hidden variant ID inputs
    const variantInputs = this.querySelectorAll('.product-variant-id');
    variantInputs.forEach(input => {
      input.value = productData.variant_id;
      input.disabled = !productData.available;
    });

    // Update quick add buttons
    const quickAddButtons = this.querySelectorAll('.quick-add__submit');
    quickAddButtons.forEach(button => {
      if (!productData.available) {
        button.disabled = true;
        const span = button.querySelector('span');
        if (span) span.textContent = soldOutText;
      } else {
        button.disabled = false;
        const span = button.querySelector('span');
        if (span) span.textContent = addToCartText;
      }
    });

    // Update modal openers with new product URL
    const modalOpeners = this.querySelectorAll('modal-opener button');
    modalOpeners.forEach(button => {
      button.setAttribute('data-product-url', productData.url);
    });
  }

  updateVariantAvailability() {
    // Get current selections to check against
    const currentSelections = this.getCurrentSelections();
    
    // Check availability for each metafield selector
    this.checkMetafieldAvailability(1, currentSelections);
    this.checkMetafieldAvailability(2, currentSelections);
    this.checkMetafieldAvailability(3, currentSelections);
  }

  checkMetafieldAvailability(metafieldNumber, currentSelections) {
    const selectors = this.querySelectorAll(`input[name^="variant-selector-${metafieldNumber}-"]`);
    let needsReselection = false;
    
    selectors.forEach(selector => {
      const optionValue = selector.nextElementSibling?.textContent?.trim();
      if (!optionValue) return;
      
      // Create hypothetical selection with this option
      const testSelections = { ...currentSelections };
      testSelections[`metafield_${metafieldNumber}`] = optionValue;
      
      // Check if ANY product matches this combination with hierarchical logic
      // This uses the same findMatchingProduct logic as the original card-product-grouped.js
      const hasMatch = this.findMatchingProduct(testSelections, metafieldNumber);
      
      const option = selector.closest('.variant-option');
      if (option) {
        if (hasMatch) {
          option.classList.remove('unavailable');
          selector.disabled = false;
        } else {
          option.classList.add('unavailable');
          selector.disabled = true;
          // If this option is currently selected but unavailable, uncheck it
          if (selector.checked) {
            selector.checked = false;
            needsReselection = true;
          }
        }
      }
    });
    
    // If we unchecked something, ensure we select the first available option
    if (needsReselection) {
      const firstAvailable = Array.from(selectors).find(s => !s.disabled);
      if (firstAvailable) {
        firstAvailable.checked = true;
      } else if (selectors.length > 0) {
        // If all are disabled, still check the first one
        selectors[0].checked = true;
      }
    }
  }

  connectedCallback() {
    // Don't check availability on initial connection - all options should be available
    // Availability will be checked after user makes selections in handleVariantChange
  }
}

// Register the custom element
  customElements.define('grouped-product-card-furnace', GroupedProductCardFurnace);

  // Initialize existing grouped product cards
  document.addEventListener('DOMContentLoaded', () => {
    const groupedCards = document.querySelectorAll('grouped-product-card-furnace');
    groupedCards.forEach(card => {
      if (!card.initialized) {
        card.initialized = true;
      }
    });
  });
} // end duplicate-load guard
